import {
  APPLICATION_ERROR_MESSAGES,
  EmploymentType,
  EnterpriseStructure,
  FINANCIAL_PRODUCT,
  IdentificationType,
  MedicareCardColour,
  OwnershipType,
  PaymentFunds,
  PensionType,
  Period,
} from '@brighte/validation';
import { css } from '@emotion/css';
import { ModifiedApplicationQuote, OpenBankingFeedBack } from '@graphql/index';
import { Box } from '@spark-web/box';
import {
  CameraIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  FingerPrintIcon,
  HomeIcon,
  IdentificationIcon,
} from '@spark-web/icon';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLink, TextLinkButton } from '@spark-web/text-link';
import { TextList } from '@spark-web/text-list';
import Exclamation from '../../assets/exclamation.svg?react';
import Tick from '../../assets/tick.svg?react';
import { formatDollars } from './helper';

export const BRIGHTE_GREEN_LOAN = 'Brighte Green Loan';
export const CASH_PAYMENT = 'Direct payment';
export const APPLICATION_SUBMITTED = 'Application submitted - what happens next';
export const PRIMARY_APPLICATION_SUBMITTED = 'We’re almost there - what happens next';

export const LABELS = {
  DWELLING_TYPE: 'Dwelling type',
  OCCUPANCY_STATUS: 'Occupancy status',
  UNIMPROVED_VALUE: 'Unimproved value',
  STREET_NUMBER: 'Street number',
  STREET_NAME: 'Street name',
  SUBURB: 'Suburb',
  STATE: 'State',
  POSTCODE: 'Postcode',
  UNIT_NUMBER: 'Unit number',
  HOME_ADDRESS: 'Home address',
  INSTALLATION_ADDRESS: 'Installation address',
  I_OWN_PROPERTY: 'I own the property at the installation address (own or have a mortgage)',
  MANUAL_ADDRESS: 'Enter address manually',
  FIRST_NAME: 'First name',
  LAST_NAME: 'Last name',
  MIDDLE_NAME: 'Middle name',
  PASSPORT_NUMBER: 'Passport number',
  ISSUING_COUNTRY: 'Issuing country',
  ISSUING_COUNTRY_PLACEHOLDER: 'Choose an issuing country',
  AUSTRALIAN_PASSPORT: 'Australian passport',
  OVERSEAS_PASSPORT: 'Overseas passport',
  DATE_OF_BIRTH: 'Date of birth',
  EXPIRY_DATE: 'Expiry date',
  IDENTIFICATION_TYPE: 'Identification type',
  MEDICARE_NUMBER: 'Medicare card number',
  MEDICARE_REFERENCE: 'Individual ref number',
  PROOF_ID: 'Concession card',
  LICENCE_NUMBER: 'Licence number',
  LICENCE_CARD_NUMBER: 'Card number',
  PHOTO_ID_NUMBER: 'ID number',
  INCOME_TYPE: 'Primary income type',
  EMPLOYER_NAME: "Employer's business name",
  EMPLOYER_PHONE_NUMBER: "Employer's phone number",
  JOB_TITLE: 'Job title',
  INCOME_AFTER_TAX: 'Primary income (after tax)',
  BNPL_INCOME_AFTER_TAX: 'Primary income (after tax)',
  NET_INCOME: 'Net income',
  ADDITIONAL_INCOME: 'Additional income',
  ADDITIONAL_INCOME_AFTER_TAX: 'Additional income (after tax)',
  TOTAL_NET_INCOME: 'Total net income',
  ABN_TRADING_NAME: 'Trading Name and ABN',
  ABN_NUMBER: 'ABN Number',
  TRADING_NAME: 'Trading Name',
  ENTER_MANUALLY: 'Enter Manually',
  BUSINESS_PHONE_NUMBER: 'Business phone number',
  RETIRED_OR_PENSIONER: 'Retired or pensioner',
  PENSION_TYPE: 'Pension type',
  ACCOUNT_HOLDER: 'Account holder name',
  BSB: 'BSB',
  ACCOUNT_NUMBER: 'Account number',
  MARITAL_STATUS: 'Relationship status',
  DEPENDANTS: 'Dependants',
  FREQUENCY: 'Frequency',
  AMOUNT: 'Amount',
  SUMMARY_HEADER: 'Summary',
  EMPLOYMENT: 'Employment status',
  IDENTIFICATION: 'Identification',
  CONFIRMED: 'Confirmed',
  PARTNER_INCOME: 'Partner income after tax',
  MORTGAGE_REPAYMENTS: 'Primary residence mortgage repayments',
  HOUSEHOLD_INCOME: 'Household income',
  RENT: 'Rent',
  INVESTMENT_PROPERTIES: 'Investment properties mortgage repayments',
  OTHER_LOANS: 'Other loans',
  CREDIT_CARDS: 'Credit card/s spending limit',
  HOUSE_LIVING_EXPENSES: 'Household living expenses',
  EMAIL: 'Email',
  MOBILE_NUMBER: 'Mobile number',
  OTP: 'Secure code',
  ENTITY_STRUCTURE: 'Entity Structure',
  BUSINESS_NAME: 'Legal business name',
  OFFICE_ADDRESS: 'Registered office address',
  PRINCIPAL_PLACE: 'Principal place of business',
  IS_SAME_AS_OFFICE: 'Same as registered office address',
  ACN_NUMBER: 'ABN / ACN number',
  ASSOCIATION_NUMBER: 'Association number',
  ASSOCIATION_NUMBER_SUB: 'The unique number that was assigned to the association when it was originally formed',
  ENTITY_STRUCTURE_GUARANTOR_STATEMENT:
    "I agree that I'm the director of this company and will also be the guarantor of this loan.",
  ENTITY_STRUCTURE_CREDIT_STATEMENT:
    'I/We declare that the credit to be provided to me/us by Brighte Capital Pty Ltd is to applied wholly or predominantly for business purposes; or investment purposes other than investment in residential property.',
  OWNERSHIP_TYPE: 'Ownership Type',
  NUMBER_OF_DIRECTORS: 'Number of Directors',
  DIRECTOR: 'Director',
  NUMBER_OF_BENEFICIAL_OWNERS: 'Number of beneficial owners',
  OWNERSHIP_PERCENTAGE: 'What is the ownership percentage?',
  CHAIRMAN_ID: 'Chairman ID',
  SECRETARY_ID: 'Secretary ID',
  TREASURER_ID: 'Treasurer ID',
  EMAIL_ADDRESS: 'Email address',
  PHONE_NUMBER: 'Phone number',
  DRIVER_LICENCE_NUMBER: 'Driver licence number',
  CARD_NUMBER: 'Card number',
  ISSUE_STATE: 'Issue state',
  CONTINUE: 'Continue',
  REQUEST_CODE: 'Request Code',
  CREDIT_CARD_HOLDER: 'Card holder name',
  CREDIT_CARD_NUMBER: 'Card number',
  FEES_AND_CHARGES: `Fees & Charges ${String.fromCharCode(0x00b2)}`,
  BANK_ACCOUNT_NAME: 'Account holder name',
  OCCUPATION_FIELD: 'Occupation',
  MANUAL_OCCUPATION: 'Enter occupation manually',
  OCCUPATION_DESCRIPTION: 'Select an occupation that best matches your role',
  NON_HOMEOWNER_CHECK:
    'As a non-homeowner, are you aware that this equipment is long-term and may not move with you if your living situation changes? ',
  CANCEL: 'Cancel',
  OTHER: 'Other',
  IS_MOST_RECENT_ADDRESS: 'I have lived at this address for the past 12 months.',
  IS_MOST_RECENT_ADDRESS_SUBTITLE:
    'If you have lived at the installation address for 12 months or less, we also need your previous address.',
  PREVIOUS_ADDRESS: 'Previous address',
  LEGAL_BUSINESS_NAME: 'Legal business name',
  ABN: 'ABN',
  BUSINESS_PHONE: 'Business phone',
  BUSINESS_EMAIL: 'Business email',
  BUSINESS_WEBSITE: 'Business website',
  YEARS_IN_TRADING: 'Years in trading',
  NUMBER_OF_EMPLOYEES: 'Number of employees',
  INDUSTRY_CATEGORY: 'Industry category',
  PROPERTY_STATUS: 'Status of property where business resides',
  RESIDENTIAL_ADDRESS: 'Residential address',
  BILLING_CONTACT: 'Billing contact',
};

export const BRIGHTE_CONTACT_NUMBER = '1300 274 448';

export const CONTACT_NUMBER_BY_FINANCIAL_PRODUCT = {
  [FINANCIAL_PRODUCT.CBA_RESIDENTIAL]: '1300 500 107',
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: '1300 135 100',
  default: BRIGHTE_CONTACT_NUMBER,
};

export const PLACEHOLDER = 'Select';

export const TIME_FREQUENCY = [
  { label: 'Yearly', value: Period.Annually },
  { label: 'Monthly', value: Period.Monthly },
  { label: 'Fortnightly', value: Period.Fortnightly },
  { label: 'Weekly', value: Period.Weekly },
];

export const PURCHASE_CATEGORY_SLUGS = {
  SOLAR_SYSTEM: 'solar-system',
  BATTERY_STORAGE: 'battery-storage',
  SOLAR_BATTERY_SYSTEM: 'solar-battery-system',
  OFF_GRID_SYSTEM: 'off-grid-system',
  INSULATION: 'insulation',
  SOLAR_HOT_WATER_SYSTEM: 'solar-hot-water-systems-incl-heat-pump',
  ELECTRIC_HEATING_COOLING: 'electric-heating-cooling-systems-reverse-cycle',
  ELECTRIC_STOVE_TOPS: 'efficient-electric-stove-tops',
  ENERGY_EFFICIENT_APPLIANCES: 'energy-efficient-appliances',
  HOT_WATER_HEAT_PUMPS: 'hot-water-heat-pumps-hwhp',
  HYDRONIC_HEAT_PUMPS: 'hydronic-heat-pumps',
};

export const APPLICATION_STEPS = {
  FINANCE_PURCHASE: 'Finance',
  ELIGIBILITY: 'Eligibility',
  IDENTIFICATION: 'Identification',
  INCOME_VERIFICATION: 'IncomeVerification',
  HOUSEHOLD: 'HouseholdDetails',
  HOUSEHOLD_CASH: 'HouseholdCashDetails',
  REPAYMENT_ACCOUNT: 'PaymentDetails',
  SUMMARY: 'SubmitApplication',
  CONTACT_DETAILS: 'ContactDetails',
  BUSINESS_DETAILS: 'BusinessDetails',
  OWNERSHIP_DETAILS: 'OwnershipDetails',
  BENEFICIAL_OWNER: 'BeneficialOwner',
  INCOME_REVIEW: 'IncomeReview',
  GUARANTOR_DETAILS: 'GuarantorDetails',
};

export const FINANCE_PURCHASE = {
  TITLE: 'Finance and purchase details',
  DESCRIPTION: "Let's get started. Please select a payment term and review the details of your finance and purchase.",
  DESCRIPTION_NO_TERM: "Let's get started. Please review the details of your finance and purchase.",
  DESCRIPTION_BRIGHTE_PAY: `Here's a summary of your finance and purchase details. Please enter your installation address below to continue.`,
  HEADING_FINANCE_TERMS: 'Finance terms',
  OTHER_PAYMENT_METHODS: 'Other payment methods',
  FINANCIAL_PRODUCT_FIELDS: {
    interestRate: 'Annual percentage rate',
    interestRateRiskBasePricing: 'Annual percentage rate from',
    financedAmount: 'Total finance required',
    depositAmount: 'Deposit',
    totalPurchaseAmount: 'Purchase amount',
    totalMinimumAmountPayable: 'Total minimum amount payable',
    establishmentFee: 'Establishment fee',
    weeklyAccountFee: 'Weekly account keeping fee',
    weeklyAccountFeeMultiCategory: 'Weekly account keeping fee applicable for each loan',
    latePaymentFee: 'Late payment fee',
    latePaymentFeeMultiCategory: 'Late payment fee applicable for each loan',
    introducerFee: 'Introducer fee',
    rebate: 'Government rebate',
    promotion: 'Promotion discount',
    subsidy: 'Estimated Subsidy (subject to income verification)',
    deposit: 'Deposit',
    financedAmountExact: 'Finance amount',
    loanTerm: 'Loan term',
    amountOnFirstRepaymentDate: 'Amount on first repayment date',
    fortnightlyRepayments: 'Fortnightly repayments',
    depositAmountExact: 'Deposit amount',
  },
  CASH_FIELDS: {
    remainingAmount: 'Remaining amount',
    depositAmount: 'Deposit',
    subsidy: 'Estimated Subsidy (subject to income verification)',
    totalPurchaseAmount: 'Purchase amount',
    promotion: 'Promotion discount',
  },
  VENDOR_FIELDS: {
    referenceNumber: 'Reference',
    mobile: 'Phone',
    email: 'Email',
    expiryDate: 'Quote expires',
  },
  // Completed-review summary (useFinanceSummary) section headings + row labels.
  REVIEW_HEADINGS: {
    whatYourePurchasing: 'Product',
    price: 'Price',
    repayments: 'Repayments',
    feesAndCharges: 'Fees & charges',
    // The live finance step renders the Brighte Pay disclaimers in StepFooter, where the fee-change
    // disclaimer is marked "2" (getDisclaimers returns [fortnightlyRepaymentAmount, feeChargeForBNPL]
    // for brighte-pay, symbols assigned by index). This heading carries the matching marker. The
    // completed review renders no footer, so it uses the plain heading above instead.
    feesAndChargesWithFootnote: `Fees & charges ${String.fromCharCode(0x00b2)}`,
    oneOff: 'One-off',
    ongoing: 'Ongoing',
    ifApplicable: 'If applicable',
    quoteDetails: 'Quote details',
    yourInstaller: 'Your installer',
  },
  REVIEW_FIELDS: {
    loanType: 'Loan type',
    amountFinanced: 'Amount financed',
    fortnightlyRepayment: 'Fortnightly repayment',
    firstRepayment: 'First repayment',
    accountKeepingFee: 'Account keeping fee',
    description: 'Description',
    expires: 'Expires',
    name: 'Name',
  },
  CATEGORY_COMPONENTS: {
    pvPanelCapacity: 'Panels',
    inverterCapacity: 'Inverter',
    batteryCapacity: 'Battery',
    hwhpTankCapacity: 'Tank capacity',
    insulationType: 'Insulation type',
    existingInsulationRValue: 'Old insulation R-Value',
    totalInsulationRValue: 'Total R-Value',
    walls: 'Walls',
    ceiling: 'Ceiling',
    floor: 'Floor',
    newInsulationRValue: 'New insulation R-Value',
    heatingCoolingSystemType: 'Heating cooling system type',
    heatingCoolingSystemCapacity: 'Heating cooling system capacity',
    inductionCooktopManufacturerName: 'Manufacturer',
    inductionCooktopModelNumber: 'Model number',
    electricCargoBikeManufacturerName: 'Manufacturer',
    electricCargoBikeModelNumber: 'Model number',
    stoveType: 'Stove type',
    insulationLocation: 'Insulation location',
  },
  NO_EARLY_REPAYMENT_FEES: 'No early repayment fees',
  MULTI_PRODUCT_DIALOG_TITLE: 'Proceed with application',
  MULTI_PRODUCT_DIALOG_CONTENT: (financialProductName: string) =>
    `Please note: if you choose to finance the balance of your purchase with a ${financialProductName}, each product will require a separate loan. However, you will only need to submit one application.`,
  DISCLAIMERS: {
    interestRate: (
      interestRate: number,
      maxInterestRate: number,
    ) => `The repayments are indicative only and have been calculated using our lowest rate based on a customer
      with an excellent credit profile. Annual percentage rates range from ${interestRate}% to ${maxInterestRate}%. 
      Rate is current at the date of publication. The final interest rate and
      repayment amount will be provided if an application for credit is approved. A higher interest rate will
      result in a higher repayment amount and total amount payable. All applications are subject to Brighte’s
      credit and eligibility criteria. The information on this page is not an offer, quote or approval of
      finance.`,

    financedAmount: () =>
      'Total finance amount may vary subject to interest rate, establishment fee and subsidy amount verification.',

    totalMinimumAmountPayable: (financialProductId?: string) => {
      if (financialProductId && financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515) {
        return `This value may change.`;
      }
      return 'This amount may be less if you make extra payments';
    },
    latePaymentFeeForFlex: () => `Later fee is charged for each fortnight of missed payment, capped at $49.90`,
    establishmentFee: (minEstablishmentFee: number, maxEstablishmentFee: number) =>
      `An establishment fee of between $${minEstablishmentFee}-$${maxEstablishmentFee} will be payable. Final establishment fee amount will be determined on submission of application. The establishment fee forms part of your fortnightly repayments.`,
    feeChargeForBNPL: (
      <Text tone="muted" size="small" className={css({ display: 'inline', '&::before': { content: 'none' } })}>
        Fees are subject to change, or new fees may be introduced. We will notify you by email at least 20 days before
        any fee change. Further information about fees and charges are outlined in the{' '}
        <TextLink href="https://brighte.com.au/agreement/" target="_blank" rel="noreferrer">
          terms & conditions
        </TextLink>
      </Text>
    ),
    fortnightlyRepaymentAmount:
      'This is an indicative amount, your actual repayment amount may be lower and will appear on your contract.',
  },
  CASH_PAYMENT_DESCRIPTION:
    'You have the option to use an independent financier if you choose to or pay the vendor directly',
  LOAN_PAYMENT_DESCRIPTION:
    'If you would like to discuss different repayment terms for each loan please call Brighte on 1300-274-448',
  PRIVACY_AGREEMENT: 'https://brighte.com.au/agreement/',
  BATTERY_ACKNOWLEDGEMENT_CHECKBOX_HEADING: 'Cheaper Home Batteries Program',
  BATTERY_ACKNOWLEDGEMENT_CHECKBOX_LABEL: (
    <Text>
      I have read and acknowledge the information about the Cheaper Home Batteries Program and{' '}
      <Text inline weight="semibold">
        risks of proceeding early
      </Text>
      , and I still wish to apply for Brighte credit and install a battery.
    </Text>
  ),
  BATTERY_ACKNOWLEDGEMENT_TITLE: 'Customer Acknowledgement – Cheaper Home Batteries Program',
  BATTERY_ACKNOWLEDGEMENT_CONTENT: (
    <Stack gap="xlarge">
      <Text>
        Before proceeding with your Brighte application for battery finance, please read and confirm the following:
      </Text>
      <TextList>
        <Text>
          I understand that no official scheme rules, application process, or eligibility criteria have been released
          from the newly elected Labor government.
        </Text>
        <Text>I understand this means the rebate is not guaranteed, and I may not receive any subsidy.</Text>
        <Text>
          I understand that if I install a battery now, I am doing so at my own risk, as the program is not active yet.
          My battery must not be commissioned (i.e., ‘turned on’) before 1 July 2025 to be eligible.
        </Text>
        <Text>
          I understand Brighte will not fund any expected rebate amount in my loan or plan. My retailer may choose to
          cover that risk, but I remain responsible for the full repayment of my Brighte credit contract, even if the
          rebate does not proceed.
        </Text>
        <Text>
          I understand that a battery installed at an off-grid or grid-connected site may qualify, and existing systems
          may still be eligible if upgraded and that eligibility depends on future rules and that equipment lists and
          rebate calculators are not yet available.
        </Text>
        <Stack gap="large">
          <Text>
            I understand the proposed rebate would be delivered via the Small-scale Renewable Energy Scheme (SRES) and
            offer approx. $370 per kWh for batteries:
          </Text>
          <TextList>
            <Text>Minimum size: 5kWh | Maximum: 50kWh</Text>
            <Text>Must be ‘VPP ready’ (but not required to join a VPP)</Text>
            <Text>Site must have existing solar panels</Text>
            <Text>
              Only one claim per address, and the system must be installed at a residential, small business or community
              facility
            </Text>
          </TextList>
        </Stack>
      </TextList>
      <Text>
        I confirm I understand all of the above, including the{' '}
        <Text weight="semibold" inline>
          risks of proceeding early
        </Text>
        , and I still wish to apply for Brighte credit and install a battery.
      </Text>
    </Stack>
  ),
  BATTERY_ACKNOWLEDGEMENT_RIGHTS: (
    <Stack gap="xlarge">
      <Text as="h2" size="large" weight="semibold">
        Your rights
      </Text>
      <TextList>
        <Text>
          You are protected under{' '}
          <Text weight="semibold" inline>
            Australian Consumer Law.
          </Text>
        </Text>
        <Stack gap="large">
          <Text>
            If this was an unsolicited sale, you have a{' '}
            <Text weight="semibold" inline>
              14-day cooling-off period
            </Text>
            .
          </Text>
          <TextList>
            <Text>
              Consumer guarantees{' '}
              <TextLink
                href="https://www.accc.gov.au/consumers/buying-products-and-services/consumer-rights-and-guarantees"
                rel="noreferrer"
                target="_blank"
              >
                Consumer rights and guarantees
              </TextLink>
            </Text>
            <Text>
              <TextLink
                href="https://consumer.gov.au/sites/consumer/files/2016/05/0553FT_ACL-guides_SalesPractices_web.pdf"
                rel="noreferrer"
                target="_blank"
              >
                Cooling-off period info
              </TextLink>
            </Text>
          </TextList>
        </Stack>
      </TextList>
    </Stack>
  ),
};

export const ELIGIBILITY = {
  TITLE: 'Eligibility confirmation',
  DESCRIPTION: 'Please confirm you are eligible to apply and consent to our privacy and electronic communication terms',
  SFA_DESCRIPTION:
    'To be eligible for ACT SFA loan, you must have permission to apply on behalf of the Owners Corporation and must provide the following:',
  HEADING_FINANCE_ELIGIBILITY: 'Application eligibility',
  HEADING_FINANCE_ELIGIBILITY_BNPL: 'Finance eligibility',
  HEADING_PRIVACY_AND_ELECTRONIC_COMMUNICATION: 'Privacy and electronic communication',
  HEADING_WHAT_YOU_NEED: "What you'll need",
  FINANCE_TERMS: {
    beOver18: 'I am over 18 and an Australian resident',
    beOver18AustralianCitizen: 'I am over 18 and an Australian citizen',
    haveAustralianIncome: 'I am employed, self-employed, retired or a pensioner',
    haveAustralianIncomeWithoutRetired: 'I am employed or self-employed',
    beActHomeOwner:
      'I am an owner of a property in ACT, or have full authority to install chosen products in a property in ACT',
    beCompletedPropertyOwner:
      'I confirm that the property is not under construction and was built more than 12 months ago',
    beTasHomeOwner:
      'I am an owner of a property in TAS, or have full authority to install chosen products in a property in TAS',
    abn: 'Body Corporate’s ABN or distinct business name',
    grantLockLetter: 'ACT govt.’s Grant Lock-in letter',
    proofOfConsent: 'Proof of consent for installation from the  Owners Corporation',
    proofOfAbility: 'Proof of ability to repay the loan',
    bankAccount: 'Bank account details for Direct Debit for loan repayment',
    businessEligibilityCriteriaAgreement: 'I agree to the eligibility criteria outlined above',
    haveActiveMortgage: 'I have an active mortgage',
    haveEligibleNetIncome: 'I have a net income over $5,000 per month',
    beActHomeOwnerAndOccupant:
      'I am the owner of a property in ACT, have full authority to install chosen products in a property in ACT and occupy the home in which the product will be installed',
    consentActTerms: (
      <Text>
        I consent to the ACT Sustainable Household Scheme{' '}
        <TextLink href="https://brighte.com.au/support/act-shs-terms" target="_blank" rel="noreferrer">
          Terms and Conditions
        </TextLink>
      </Text>
    ),
    businessTrading2Years: 'The business must have been trading for more than 2 years',
    businessBankAccount: 'The business must have a current Australian bank account',
    businessNoJudgements: 'The company cannot have any judgements against it for non-payment of debts',
    businessValidABNGST: 'The company must have a valid Australian Business Number (ABN) and be registered for GST',
    applicantPermanentResident: 'All applicants must be Australian permanent residents',
    applicantOver18: 'Over 18 years of age',
    businessNoBankruptcies: 'No bankruptcies - for Company or Director',
    guarantorOwnsHome: 'Guarantor must own or be purchasing a home',
    businessNoLiquidation: 'No liquidation or external administration',
    businessNoTaxDebt: 'No amounts owed to the Australian Taxation Office',
    actCommunityNotForProfit:
      'Be a not-for-profit community organisation that operates in the ACT as evidenced by a utility bill or lease agreement',
    actCommunityHaveAbn: 'Have an Australian Business Number (ABN)',
    actCommunityBoardRep:
      "Represented by the chief executive, or another representative appointed by the organisation's board",
    actCommunityIncorporatedStructure:
      'Have an incorporated legal structure (e.g., an incorporated association, a company limited by guarantee that is registered under the Corporations Act 2001, an incorporated indigenous corporation or an incorporated non-distributing co-operative)',
    actCommunityPurchaseForACT: "Purchase products for installation on the organisations' premises within the ACT",
    actCommunityParticipateProgram: (
      <Stack gap="small">
        <Text>{'Participate in either :'}</Text>
        <Box paddingTop="small">
          <TextList>
            <Text>{'An Actsmart workshop or,'}</Text>
            <Text>
              {
                'The Business Energy and Water Program to undertake a free energy assessment2. This program provides rebates of up to $5,000 for eligible small to medium businesses to upgrade to water-efficient and energy-efficient technologies'
              }
            </Text>
          </TextList>
        </Box>
      </Stack>
    ),
    actCommunityApplicantsMust: (
      <Stack gap="small">
        <Text>{'All applicants must :'}</Text>
        <Box paddingTop="small">
          <TextList>
            <Text>{'be Australian permanent residents'}</Text>
            <Text>{'over 18 years of age'}</Text>
            <Text>{'No bankruptcies - for Company or Director'}</Text>
            <Text>{'No liquidation or external administration'}</Text>
            <Text>{'No amounts owed to the Australian Taxation Office'}</Text>
          </TextList>
        </Box>
      </Stack>
    ),
  },
  PRIVACY_TERMS: {
    consentPrivacyPolicy: (
      <Text>
        I consent to the collection, use and disclosure of my personal information by Brighte as set out in the{' '}
        <TextLink href="https://brighte.com.au/privacy/" target="_blank" rel="noreferrer">
          privacy policy
        </TextLink>
      </Text>
    ),
    consentCreditReport:
      'I consent to Brighte providing its Credit Guide, loan contract documents and any offer to me in electronic form',
    consentCreditReportBNPL:
      'I understand that the information provided on this application will be used to make a credit enquiry and may alter my credit score',
    consentCashCreditReport:
      'I consent to Brighte providing subsidy documentation and any offer to me in electronic form.',
    consentTasTerms: (
      <Text>
        I consent to the Tasmanian Energy Saver Loan Scheme{' '}
        <TextLink href="https://brighte.com.au/support/tas-esls-terms" target="_blank" rel="noreferrer">
          Terms and Conditions
        </TextLink>
      </Text>
    ),
    creditAndTermsAgreement: (
      <Text>
        I have read Brighte's{' '}
        <TextLink href="https://brighte.com.au/support/credit-guide" target="_blank" rel="noreferrer">
          Credit Guide
        </TextLink>{' '}
        and{' '}
        <TextLink
          href="https://brighte.com.au/support/brighte-personal-loan-agreement"
          target="_blank"
          rel="noreferrer"
        >
          Terms and Conditions
        </TextLink>
      </Text>
    ),
    creditAndTermsBheslAgreement: (
      <Text>
        I have read Brighte's{' '}
        <TextLink href="https://brighte.com.au/support/credit-guide" target="_blank" rel="noreferrer">
          Credit Guide
        </TextLink>{' '}
        and{' '}
        <TextLink href="https://brighte.com.au/support/home-energy-saver-loan-terms" target="_blank" rel="noreferrer">
          Terms and Conditions
        </TextLink>
      </Text>
    ),
    creditAndTermsFlexAgreement: (
      <Text>
        I agree to the{' '}
        <TextLink href=" https://brighte.com.au/support/brighte-flex-terms" target="_blank" rel="noreferrer">
          terms and conditions{' '}
        </TextLink>
        and wish to make an offer to enter into a contract for finance with Brighte
      </Text>
    ),
    consentFlexCreditCheck:
      'I understand that the information provided on this application will be used to make a credit enquiry and may alter my credit score',
    consentCreditCheck:
      'I consent to the information provided on this application will be used to make a credit enquiry and may alter my credit score.',
    consentElectrify2515Terms: (
      <Text>
        I consent to the Electrify 2515 Community Pilot{' '}
        <TextLink href="https://brighte.com.au/electrify-2515-terms" target="_blank" rel="noreferrer">
          terms and conditions
        </TextLink>
      </Text>
    ),
    creditAndTermsBNPLAgreement: (
      <Text>
        I agree to the{' '}
        <TextLink href="https://brighte.com.au/agreement" target="_blank" rel="noreferrer">
          terms and conditions{' '}
        </TextLink>
        and wish to make an offer to enter into a contract for finance with Brighte
      </Text>
    ),
    haveReadDisclosureOfCreditInformation: (
      <Text>
        Before finalising, be sure to read the{' '}
        <TextLink href="https://brighte.com.au/support/pre-contractual-disclosure" target="_blank" rel="noreferrer">
          disclosure
        </TextLink>{' '}
        for important credit info
      </Text>
    ),
    consentNswTerms:
      'You consent to Brighte collecting your NMI/DPI data, address details and personal information related to the Home Energy Saver Loan Program and to this data being collected by Brighte, and provided to the NSW Government (via Brighte and the Energy Security Corporation), for purposes including measurement and verification, contacting you for feedback and evaluation of the program.',
  },
  NSW_PROGRAM_QUESTIONS: {
    HEADING: 'NSW program questions',
    SUBHEADING: 'The NSW Government requires us to collect the following information.',
    YES: 'Yes',
    NO: 'No',
    QUESTIONS: {
      sresClaimed:
        'Did your installer discount their quote to account for the Small-scale Technology Certificate (STC) rebate?',
      nswSafeguardClaimed: 'Has the NSW Energy Savings Scheme been factored into the cost of your installation?',
      hesdClaimed: 'Have you claimed the Home Energy Saver Discount for this installation?',
      nathersAssessment: 'Has a NatHERS home energy rating assessment been completed for your property?',
      replacesExistingEquipment: 'Does this installation replace existing equipment?',
    },
    TOOLTIP: {
      sresClaimed:
        'Small-scale Technology Certificates (STCs) are a federal government rebate for solar and eligible systems. Most installers apply this as an upfront discount on your quote rather than paying it separately.',
      nswSafeguardClaimed:
        'The NSW Energy Savings Scheme creates financial incentives for energy efficiency upgrades. Some installers factor these into their pricing - check your quote or ask your installer.',
      nathersAssessment:
        "NatHERS is the Nationwide House Energy Rating Scheme. A NatHERS assessment rates your home's energy efficiency. Not all installations require one - your installer will advise if it applies.",
    },
  },
  SFA_TERMS: {
    abn: 'Body Corporate’s ABN or distinct business name',
    grantLockLetter: 'ACT govt.’s Grant Lock-in letter',
    proofOfConsent: 'Proof of consent for installation from the  Owners Corporation',
    proofOfAbility: 'Proof of ability to repay the loan',
    bankAccount: 'Bank account details for Direct Debit for loan repayment',
    businessEligibilityCriteriaAgreement: 'I agree to the eligibility criteria outlined above',
  },

  WHAT_YOU_NEED: {
    needBiometricProofTypes: {
      icon: <IdentificationIcon size="small" tone="primary" />,
      text: 'An Australian Drivers Licence or Passport.',
    },
    needProofTypes: {
      icon: <IdentificationIcon size="small" tone="primary" />,
      text: 'An Australian Drivers Licence, Passport or Medicare card.',
    },
    needIncomeProofOptional: {
      icon: <DocumentTextIcon size="small" tone="primary" />,
      text: 'Proof of income may be required.',
    },
    needIncomeProof: {
      icon: <DocumentTextIcon size="small" tone="primary" />,
      text: 'Proof of income',
    },
    needConcessionProof: {
      icon: <CreditCardIcon size="small" tone="primary" />,
      text: 'Evidence of Veteran Gold Card, Pensioner Concession Card or Health Care Card.',
    },
    needBiometricCheck: {
      icon: <CameraIcon size="small" tone="primary" />,
      text: "For added security, a Biometric Identity check may be required. This includes comparing a video of yourself with your photo ID. If your finance application is approved, we'll send you a link for you to complete this mandatory step before finalising your application.",
    },
    // TO DO: ask if we can make it generic for all products and merge above text into this one: according https://brighte.atlassian.net/browse/CT-847
    needBiometricCheckOptional: {
      icon: <FingerPrintIcon size="small" tone="primary" />,
      text: 'Biometric identity check may be required.',
    },
  },
  UNSOLICITED_SALES: 'How did you make initial contact with',
  UNSOLICITED_SALES_OPTIONS: [
    {
      label: 'I contacted the vendor directly following my own research.',
      value: 'I contacted the vendor directly following my own research.',
    },
    {
      label: "I found the vendor using Brighte's marketplace.",
      value: "I found the vendor using Brighte's marketplace.",
    },
    {
      label: 'The vendor called me unexpectedly offering their products.',
      value: 'The vendor called me unexpectedly offering their products.',
    },
    {
      label: 'The vendor came to my door unexpectedly offering their products.',
      value: 'The vendor came to my door unexpectedly offering their products.',
    },
    {
      label: 'I found the vendor after seeing a digital ad (Facebook, Google, etc.) or another form of advertisement.',
      value: 'I found the vendor after seeing a digital ad (Facebook, Google, etc.) or another form of advertisement.',
    },
  ],
  CUSTOMER_DECLARATION_HEADING: 'Customer declaration',
  // These two say the same thing and must stay in step. POINT is what the customer reads — shown as a
  // bullet above an "I acknowledge the above" checkbox. DESCRIPTION is the grammatically complete
  // sentence persisted as `consentCustomerDeclaration` (see StepNavigator/helper.ts), so it stays
  // first person. Changing the wording of one means changing the other.
  CUSTOMER_DECLARATION_DESCRIPTION:
    'I acknowledge that the information provided in this application form has been completed by me, the applicant, in my personal capacity and on my own behalf.',
  CUSTOMER_DECLARATION_POINT:
    'The information provided in this application form has been completed by me, the applicant, in my personal capacity and on my own behalf',
  I_ACKNOWLEDGE: 'I acknowledge the above',
  AGREE_TO_ALL: 'I agree to all of the above',
  DECLARATION_DIRECTOR_GUARANTOR:
    'I agree that I’m the director of this company and will also be the guarantor of this loan (if otherwise, you may not be able to complete this application)',
  DECLARATION_CREDIT_BUSINESS:
    'I/We declare that the credit to be provided to me/us by Brighte Capital Pty Ltd is to be applied wholly or predominantly for business purposes; or investment purposes other than investment in residential property',
  CONSENT_MARKETING_INFO:
    'I/We consent to receive information about goods and services that may be of interest to me/us',
  SME_TERMS_AND_CONDITIONS: (
    <Text>
      I have read Brighte's{' '}
      <TextLink href="https://brighte.com.au/support/brightepay-terms-and-conditions" target="_blank" rel="noreferrer">
        Terms and Conditions
      </TextLink>
    </Text>
  ),
  ACT_COMMUNITY_TERMS_AND_CONDITIONS: (
    <Text>
      I have read Brighte's{' '}
      <TextLink href="https://brighte.com.au/support/brighte-business-loan-contract" target="_blank" rel="noreferrer">
        Terms and Conditions
      </TextLink>
    </Text>
  ),
  ACT_COMMUNITY_SHS_CONSENT: (
    <Text>
      I consent to the ACT Sustainable Household Scheme{' '}
      <TextLink href="https://brighte.com.au/support/act-shs-terms" target="_blank" rel="noreferrer">
        terms
      </TextLink>
    </Text>
  ),
};

export const IDENTIFICATION = {
  TITLE: 'Identification details',
  DESCRIPTION: 'Select an identification type and enter your details exactly as they appear.',
  SAME_ADDRESS: 'Same as installation address',
  MEDICARE_CARD_DESCRIPTION: 'This is 10 digits number on your card',
  MEDICARE_REF_DESCRIPTION: 'This is printed against your name',
  IDENTIFICATION_LICENCE_DESCRIPTION: {
    ACT: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION: 'This is printed on the front of your card at the bottom next to your photo',
    },
    NT: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION:
        "This is printed on the back of your card at the bottom and labeled as 'Card Number'",
    },
    NSW: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the top right and labeled as 'Card Number'",
    },
    QLD: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the top right and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the bottom center and labeled as 'Card Number'",
    },
    SA: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION: 'This is printed on the front of your card at the top right',
    },
    TAS: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION: 'This is printed on the front of your card at the top right',
    },
    VIC: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION: 'This is printed on the front of your card at the top right',
    },
    WA: {
      LICENCE_NUMBER_DESCRIPTION:
        "This is printed on the front of your card at the left and labeled as 'Licence Number'",
      LICENCE_CARD_NUMBER_DESCRIPTION: 'This is printed on the front of your card at the top right',
    },
  },
  IDENTIFICATION_OPTIONS: [
    { label: 'Passport', value: IdentificationType.PASSPORT },
    { label: 'Drivers Licence', value: IdentificationType.DRIVING_LICENSE },
    { label: 'Medicare', value: IdentificationType.MEDICARE },
    { label: 'State Issued Photo ID', value: IdentificationType.PHOTO_ID },
  ],
  MEDICARE_CARD_COLOUR_OPTIONS: [
    { label: 'Blue', value: MedicareCardColour.Blue },
    { label: 'Green', value: MedicareCardColour.Green },
    { label: 'Yellow', value: MedicareCardColour.Yellow },
  ],
  PROOF_ID_DESCRIPTION: (financialProductId: string) => {
    const description = {
      [FINANCIAL_PRODUCT.ACT_SLIP]:
        "To be eligible for ACT Home Energy Support Rebate, you must upload a picture of your concession card, e.g. Government Pensioner Concession Card, Department of Veterans' Affairs Gold Card, Health Care Card. Ensure that all card details are visible in the uploaded document.",
      [FINANCIAL_PRODUCT.ELECTRIFY_2515]:
        'Please upload a clear copy of your concession card, such as the Government Pensioner Concession Card, Department of Veteran Affairs Gold card, or Health Care Card. Ensure all the card details are visible in the uploaded documents.',
    };
    return (
      description[financialProductId] ??
      'To be eligible, you must upload a proof of your ID. Ensure that all details are visible in the uploaded document.'
    );
  },
  EXISTING_USER_NOTE:
    "we've pre-filled some of your details for you. If this isn't accurate, please contact us on 1300-BRIGHTE for assistance.",
  IS_CONCESSION_CARD_HOLDER: 'I am a concession card holder',
  SKIP_DOCUMENTS_WARNING:
    "By uploading now, we'll action your application faster. Otherwise, we'll email you so you can provide us with your identification documents in the next 48 hours.",
  DEFAULT_LICENCE_NUMBER_DESCRIPTION:
    'This is printed on the front of your card at the left and labeled as “License Number”',
  DEFAULT_LICENCE_CARD_NUMBER_DESCRIPTION:
    'This is printed on the front of your card at the top right and labeled as “Card Number”',
  ISSUE_STATE: 'Issue state',
  STATE_OR_TERRITORY: 'State or Territory',
  CARD_COLOUR: 'Card colour',
};

export const INCOME_VERIFICATION = {
  TITLE: 'Income details',
  DESCRIPTION: 'Declare your personal income earned after tax, this is specific to you as the individual applicant',
  DESCRIPTION_CHALLENGER: 'Tell us about your personal income so we can assess your application.',
  INCOME_OPTIONS: [
    { label: 'Employed', value: EmploymentType.EMPLOYED },
    { label: 'Self Employed', value: EmploymentType.SELF_EMPLOYED },
    { label: 'Retired or pensioner', value: EmploymentType.PENSIONER },
    { label: 'Other', value: EmploymentType.OTHER },
  ],
  INCOME_AFTER_TAX_DESCRIPTION: 'The amount paid into your bank account.',
  INCOME_DOCUMENT_DESCRIPTION: 'We need to verify your income with a recent supporting document.',
  ADDITIONAL_INCOME_OPTIONS_DESCRIPTION: 'Do you have other sources of income, such as rental or freelance income?',
  SKIP_DOCUMENTS_WARNING:
    "By uploading now, we'll action your application faster. Otherwise, we'll email you so you can provide us with your two most recent payslips in the next 48 hours.",
  BANK_STATEMENT_PROVIDED: 'Bank statement provided',
  DOCUMENTS_PROVIDED: 'Success! Your document/s have been attached',
  DOCUMENTS_REQUIRED: 'Income verification documents are required',
  PROVIDE_MORE_VERIFICATION: 'Provide more verification',
  PENSIONER_OPTIONS: [
    { label: 'Aged Pension', value: PensionType.PENSIONER },
    { label: 'Disability Support Pension', value: PensionType.DISABILITY },
    { label: 'Veteran', value: PensionType.VETERAN },
    { label: 'Self-funded Retiree', value: PensionType.SELF_FUNDED_RETIREE },
    { label: "Carer's Pension", value: PensionType.CARERS_PENSION },
  ],
  SAME_AS_MOBILE: 'Same as my mobile number',
  BANK_STATEMENT_SELF_EMPLOYED_INFO:
    'You will need to verify your income by providing your most recent individual tax return',
  BANK_STATEMENT_PENSIONER_INFO:
    'You will need to verify your income by providing bank statements or a recent pension statement',
  BANK_STATEMENT_INFO: 'You will need to verify your employment income by providing your two most recent payslips',
  INCOME_WARNING: 'Income verification is required for this application.',
  UPLOAD_SUPPORTING_DOCUMENTS: 'Income verification',
  UPLOAD: 'Upload Documents',
  VERIFICATION: 'Verification',
  BANK_STATEMENT: 'Bank statement provided',
  DOCUMENTS: 'Documents provided',
  ABN_INVALID: 'Please enter a valid ABN',
  VERIFY_PRIMARY_INCOME: 'Upload documents',
  EMPLOYMENT_TYPE: 'Employment Type',
  INCOME_INVALID: 'The household income provided does not meet eligibility for this finance product at this time',
  UPLOAD_DOCUMENTS_ADDITIONAL_TEXT_WARNING:
    'Please remove or blackout your tax file number before uploading your documents.',
  AUTOFILL_WITH_CDR_TITLE: 'Connect your bank account',
  AUTOFILL_WITH_CDR_TITLE_CHALLENGER: 'Verify via your bank',
  AUTOFILL_WITH_CDR_DESCRIPTION: ['No paperwork needed', 'Read-only & bank-grade encrypted', 'Takes ~3 minutes'],
  AUTOFILL_WITH_CDR_DESCRIPTION_CHALLENGER: [
    'Fastest way to verify income for your application',
    'Secure (CDR), Australian Government-regulated',
    'Takes ~3 minutes',
  ],
  AUTOFILL_WITH_CDR_ERROR_BANNER_MESSAGE: 'Please try again—there was a problem connecting to your bank.',
  AUTOFILL_WITH_CDR_ERROR_TITLE: 'Connect to a different bank account?',
  AUTOFILL_WITH_CDR_ACTION: 'Verify now',
  AUTOFILL_WITH_CDR_FOOTER: 'Instant verification',
  AUTOFILL_WITH_CDR_FOOTER_ACTION: 'Learn more',

  INCOME_VERIFYING_WITH_FISKIL: 'Your income will be verified via Fiskil',
  INCOME_VERIFYING_WITH_FISKIL_FAILURE: 'Unable to verify the following accounts',
  AUTOFILL_WITH_CDR_COMPLETED_TITLE: 'Connected to Fiskil',
  AUTOFILL_WITH_CDR_FAST_TRACK_TEXT: 'Connect your bank account',
  UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION: () => (
    <Stack gap="large">
      <Text>We accept any of the following as proof of income:</Text>
      <Box paddingX="medium">
        <TextList type="bullet" gap="large">
          <Text>Two payslips from the last 3 months</Text>
          <Text>Bank statement covering the last 90 days</Text>
          <Text>
            Notice of Assessment (<strong>personal only</strong>, within 18 months)
          </Text>
          <Text>
            Income Tax Return (<strong>personal only</strong>, within 18 months)
          </Text>
        </TextList>
      </Box>
    </Stack>
  ),
  UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION_BY_EMPLOYMENT_TYPE: {
    [EmploymentType.EMPLOYED]: () => (
      <Stack gap="large">
        <Text>
          To verify your income, upload <strong>one</strong> of these:
        </Text>
        <Box paddingX="medium">
          <TextList type="bullet" gap="large">
            <Text>Two recent payslips (from the last 3 months)</Text>
            <Text>A personal Notice of Assessment (from the last 18 months)</Text>
          </TextList>
        </Box>
      </Stack>
    ),
    [EmploymentType.SELF_EMPLOYED]: () => (
      <Stack gap="large">
        <Text>
          To verify your income, upload <strong>one</strong> of these:
        </Text>
        <Box paddingX="medium">
          <TextList type="bullet" gap="large">
            <Text>A personal Notice of Assessment (from the last 18 months)</Text>
            <Text>A personal Individual Tax Return (from the last 18 months)</Text>
          </TextList>
        </Box>
      </Stack>
    ),
    [EmploymentType.PENSIONER]: () => (
      <Stack gap="large">
        <Text>
          To verify your income, upload <strong>one</strong> of these:
        </Text>
        <Box paddingX="medium">
          <TextList type="bullet" gap="large">
            <Text>A pension statement</Text>
            <Text>A Centrelink statement</Text>
            <Text>A superannuation statement</Text>
            <Text>A bank statement (covering the last 3 months)</Text>
            <Text>A personal Notice of Assessment (from the last 18 months)</Text>
            <Text>A personal Individual Tax Return (from the last 18 months)</Text>
          </TextList>
        </Box>
      </Stack>
    ),
    [EmploymentType.OTHER]: () => (
      <Stack gap="large">
        <Text>
          To verify your income, upload <strong>one</strong> of these:
        </Text>
        <Box paddingX="medium">
          <TextList type="bullet" gap="large">
            <Text>Two recent payslips (from the last 3 months)</Text>
            <Text>A personal Notice of Assessment (from the last 18 months)</Text>
            <Text>A personal Individual Tax Return (from the last 18 months)</Text>
            <Text>A bank statement (covering the last 3 months)</Text>
          </TextList>
        </Box>
      </Stack>
    ),
  } as Record<EmploymentType, () => JSX.Element>,
  UPLOAD_ADDITIONAL_INCOME_DOCUMENTS_DESCRIPTION: () => (
    <Stack gap="xxlarge">
      <Text>Please upload proof of your additional income covering 2-months, such as:</Text>
      <Box paddingX="medium">
        <TextList type="bullet" gap="large">
          <Text>Bank statements</Text>
          <Text>Payslips</Text>
          <Text>Income Tax Return or Notice of Assessment</Text>
          <Text>Property management statement</Text>
        </TextList>
      </Box>
    </Stack>
  ),
  AUTOFILL_WITH_CDR_COMPLETED_BANNER_TEXT: () => (
    <Stack gap="xxlarge" paddingY="small">
      <Text size="standard">Great! We are calculating your verified income.</Text>
      <Text size="standard">Please continue to the next step. You can review your totals later in Step 8.</Text>
    </Stack>
  ),
  AUTOFILL_WITH_CDR_COMPLETED_FOOTER_DESCRIPTION: (onClick: () => void) => (
    <Text size="standard">
      Receive income elsewhere?{' '}
      <TextLinkButton
        tone="primaryActive"
        weight="regular"
        onClick={onClick}
        data={{ testid: 'open-banking-connect-additional-accounts' }}
      >
        Connect additional accounts
      </TextLinkButton>{' '}
      to increase your total assessable income.
    </Text>
  ),

  AUTOFILL_WITH_CDR_COMPLETED_CHANGE_INCOME_FOOTER_TITLE: 'Connect to a different bank account?',
  AUTOFILL_WITH_CDR_COMPLETED_CHANGE_INCOME_FOOTER_DESCRIPTION: 'This will replace any income we’ve identified above.',
  AUTOFILL_WITH_CDR_COMPLETED_CHANGE_INCOME_FOOTER_ACTION: 'Connect now',
  AUTOFILL_WITH_CDR_COMPLETED_CHANGE_INCOME_FOOTER_ACTION_DESCRIPTION: 'Instant verification',

  MANUAL_INPUT_TITLE: 'Manual verification',
  MANUAL_INPUT_TITLE_UPLOAD_INCOME_DOCUMENTS: 'Upload income documents',
  MANUAL_INPUT_DESCRIPTION_OCCUPATION_GATE: [
    'Payslips, Notice of Assessment or Income Tax Return',
    'Can take up to ~48 hours',
  ],
  MANUAL_INPUT_DESCRIPTION: [
    'Tell us your income situation',
    'Upload payslip or tax assessment',
    'Can take up to ~48 hours',
  ],
  // Tailors the manual-verification card's "Upload …" bullet to the applicant's declared employment
  // type (mirrors UPLOAD_PRIMARY_INCOME_DOCUMENTS_DESCRIPTION_BY_EMPLOYMENT_TYPE). Falls back to the
  // generic MANUAL_INPUT_DESCRIPTION bullet when the type is unset or unmapped.
  MANUAL_UPLOAD_BULLET_BY_EMPLOYMENT_TYPE: {
    [EmploymentType.EMPLOYED]: 'Upload payslips or tax assessment',
    [EmploymentType.SELF_EMPLOYED]: 'Upload tax assessment',
    [EmploymentType.PENSIONER]: 'Upload bank statement or tax assessment',
  } as Partial<Record<EmploymentType, string>>,
  MANUAL_INPUT_FOOTER: '48 hours processing time',
  MANUAL_INPUT_ACTION: 'Fill out form',
  MANUAL_INPUT_ACTION_UPLOAD_DOCUMENTS: 'Upload documents',
  MANUAL_INPUT_VERIFY_INCOME_AUTOMATICALLY: 'Prefer to verify your income automatically?',
  VERIFY_YOUR_INCOME: 'Verify your income',
  VERIFY_YOUR_INCOME_DESCRIPTION:
    'To approve your loan, we need to verify your income. Choose between connecting your bank account and uploading your income documents.',
  VERIFY_YOUR_INCOME_DESCRIPTION_CHALLENGER: "Choose how you'd like to verify your income",
  VERIFY_YOUR_INCOME_MISSING_ERROR_MESSAGE: 'Please complete income verification',

  CDR_EXPLANATION_TITLE: 'How fast-track verification works',
  CDR_EXPLANATION_DESCRIPTION:
    'You can now choose to securely share a one-time summary of your bank information with us. This helps us approve your loan faster and more accurately. You stay in full control of what is shared and for how long.',
  CDR_QUESTION_1: 'Is it safe to share my banking data?',
  CDR_QUESTION_1_ANSWER:
    'Yes. Your information is protected by strict safety rules set by the Australian Government. We use high-level security (encryption) to ensure your data is shared safely between your bank and our secure system.',
  CDR_QUESTION_2: 'What information do you see and why?',
  CDR_QUESTION_2_ANSWER:
    'We only see a "view-only" summary of your money coming in and out. This helps us understand your financial situation so we can approve your loan. We cannot make any changes to your bank account or move your money.',
  CDR_QUESTION_3: 'Can I change my mind or withdraw consent later?',
  CDR_QUESTION_3_ANSWER:
    'Yes. You can stop sharing your information at any time through your bank. Once the agreed time is up, or if you withdraw your permission, we will no longer have access to your data.',
  CDR_EXPLANATION_TITLE_CHALLENGER: 'How bank verification works (Open Banking / CDR)',
  CDR_EXPLANATION_DESCRIPTION_CHALLENGER:
    'To verify your income, you can connect your bank using Open Banking (CDR). We’ll receive a read-only summary of your income and regular spending. It usually takes about 3 minutes. You’re in control, and you can stop sharing at any time.',
  CDR_QUESTION_1_CHALLENGER: 'Is Open Banking (CDR) safe?',
  CDR_QUESTION_1_ANSWER_CHALLENGER:
    'Yes. Open Banking is part of Australia’s Consumer Data Right (CDR). It’s regulated by the Australian Government and uses strong security standards. We only access what you approve.',
  CDR_QUESTION_2_CHALLENGER: 'What will you see?',
  CDR_QUESTION_2_ANSWER_CHALLENGER:
    'We only view a summary of your income and regular spending. This helps us confirm your income. We can’t change anything or move money.',
  CDR_QUESTION_3_CHALLENGER: 'What is this ‘bank linking’ thing?',
  CDR_QUESTION_3_ANSWER_CHALLENGER:
    'It’s a secure, government-regulated way to share bank information with your permission. You choose what’s shared and for how long.',
  CDR_QUESTION_4: 'How do I stop sharing later?',
  CDR_QUESTION_4_ANSWER:
    'You can stop sharing anytime. Use the email you received after linking your bank and tap “Manage access”, or stop it through your bank. Sharing turns off automatically after 1 month.',
  INCOME_VERIFICATION_SUMMARY: 'Income verification',
  INCOME_VERIFICATION_VIA_CDR_SUMMARY: 'Via Fiskil Connect',
  FEEDBACK_PROBLEMS_TITLE: 'Help us improve',
  FEEDBACK_PROBLEMS_SUBTITLE:
    'We are always trying our best to improve our products and your feedback can help us do that',
  FEEDBACK_PROBLEMS_FEEDBACK: 'Did you have any problems connecting to your bank account?',
  FEEDBACK_UNSELECTED: 'Please select an option',
  FEEDBACK_PROBLEMS_OPTIONS: [
    OpenBankingFeedBack.PROBLEM_LOGGING_IN,
    OpenBankingFeedBack.PREFER_MANUAL,
    OpenBankingFeedBack.TOO_COMPLICATED,
    OpenBankingFeedBack.OTHER,
  ],
  FEEDBACK_PROBLEMS_OPTIONS_LABELS: {
    [OpenBankingFeedBack.PROBLEM_LOGGING_IN]: 'Problems logging in',
    [OpenBankingFeedBack.PREFER_MANUAL]: 'Prefer to enter your details manually',
    [OpenBankingFeedBack.TOO_COMPLICATED]: 'Too complicated',
    [OpenBankingFeedBack.OTHER]: 'Other',
  },
  OPEN_BANKING_INSTRUCTION: {
    HEADER: 'Connect your bank account',
    STEP_ONE: 'Securely connect to your bank',
    STEP_TWO: 'Pick your accounts',
    STEP_THREE: 'Income verified automatically',
    ACTION: 'Connect now',
    LOADING: 'Loading',
    LOADING_VIDEO: 'Loading video...',
    VIDEO_UNAVAILABLE: 'Video unavailable',
    ACCOUNT_INFORMATION: 'Select the account/s where your money is paid to (salary, rental income, pension, etc).',
  },
  OPEN_BANKING_INSTRUCTION_CHALLENGER: {
    HEADER: 'Securely verify your income',
    STEP_ONE: 'Securely connect to your bank',
    STEP_TWO: 'Choose where your income is paid',
    STEP_THREE: 'Income verified automatically',
    ACCOUNT_INFORMATION:
      'Select the account where your income is paid (salary, rent, pension). This helps us verify your income accurately.',
  },
  TOTAL_VERIFIED_INCOME: 'Total estimated monthly take-home income:',
  INCOME_SYNC_SUCCESS_TITLE: 'Review your connected accounts',
  ADDITIONNAL_OPEN_BANKING_INCOME: 'Update your income',
  ADDITIONNAL_OPEN_BANKING_INCOME_DESCRIPTION:
    'Connect more accounts to capture your full income, or switch to manual verification.',
  IMCOME_SYNC_ERROR_TITLE: 'We couldn’t verify your income',
  IMCOME_SYNC_ERROR_DESCRIPTION: 'To continue with income verification, please verify your income manually.',
  VERIFY_INCOME_MANUALLY: 'Verify income manually',
  SKIP_PRIMARY_INCOME: 'Skip income verification',
  SKIP_PRIMARY_INCOME_DESCRIPTION:
    'Tick this box if you are ONLY receiving a subsidised smart energy device. You may also tick this box if you do not want to provide income documents to Brighte, however you will only receive the lowest subsidy amount.',
  MANUAL_VERIFICATION_SUBMITTED:
    'Your documents have been submitted for manual verification. The Brighte team will follow up for any further info if needed.',
};

export const OPEN_BANKING_ORIGIN = 'https://auth.fiskil.com';

export const OPEN_BANKING_INCOME_DETAILS = {
  TITLE: 'Confirm income details',
  TITLE_CARD: 'Income verified using Fiskil',
  EDIT_INCOME: 'Need to make changes?',
  EMPTY_TITLE: 'We were unable to assess your income',
  EMPTY_TITLE_BUSINESS: 'We were unable to assess your business accounts',
  EMPTY_DESCRIPTION: 'Connect to another account or enter your income manually.',
  EMPTY_DESCRIPTION_ALREADY_ASSESSED: 'Your selected account has already assessed during your partners application.',
  EMPTY_DESCRIPTION_CONTACT: 'Please contact to another account or enter your income manually.',
  EMPTY_BUTTON: 'Change income verification',
  LOADING_TITLE: 'Verifying your income details',
  LOADING_TITLE_POLLING: "We're still reviewing your income details",
  LOADING_SUBTITLE: 'This usually takes a few minutes - thanks for your patience',
  LOADING_SUBTITLE_POLLING: "It's taking a little longer than usual - we'll keep you posted shortly",
  LOADING_TITLE_3MIN_QUESTION: 'Still working on it',
  LOADING_SUBTITLE_3MIN: 'This is taking longer than expected. Would you like to keep waiting or try another option?',
  LOADING_TITLE_PATIENCE: 'Thanks for your patience',
  LOADING_SUBTITLE_PATIENCE: "We're still retrieving your income details - we'll let you know once it's done",
  LOADING_TITLE_CANCEL: "We couldn't verify your income",
  LOADING_SUBTITLE_CANCEL: 'You can connect a different account or enter your income manually',
  CONNECTED_ACCOUNTS: 'CONNECTED ACCOUNTS',
  VERIFIED_INCOME: 'Verified income',
  INCOME_LOOKS_CORRECT: 'Is this estimate correct?',
  YES: 'Yes',
  NO: 'No',
  HOW_WAS_INCOME_ESTIMATED: 'How was this calculated?',
  INCOME_DIFFERENCES_MESSAGE:
    "Small difference in income can be expected and usually won't impact your finance application.",
  DIFFERENT_BANK_ACCOUNT_TITLE: 'Need to connect to a different bank account?',
  DIFFERENT_BANK_ACCOUNT_DESCRIPTION: "This will replace any income we've identified above.",
  CHANGE_INCOME_VERIFICATION: 'Change income verification',
  FIELD_MAP: {
    INCOME_SALARY: 'Salary',
    INCOME_INVESTMENTS: 'Investments',
    INCOME_CHILD_SUPPORT: 'Child support',
    INCOME_RENTAL: 'Rental',
    INCOME_GOVERNMENT_BENEFITS: 'Government benefits',
  },
  OPEN_BANKING_ERROR_TYPE: {
    NO_INCOME: {
      HEADER: 'Your income couldn’t be verified',
      TEXT_ONE: 'We couldn’t identify a regular income in your selected account.',
      TEXT_TWO: 'Please connect to a different account or enter your income manually.',
      ACTION: 'Change income verification',
    },
    BUSINESS_ACCOUNT: {
      HEADER: 'Your income couldn’t be verified',
      TEXT_ONE: 'It looks like you connected to your business account.',
      TEXT_TWO: 'Please connect to your personal account where your income is paid, or enter your income manually.',
      ACTION: 'Change income verification',
    },
    DUPLICATE_ACCOUNT: {
      HEADER: 'Your selected account has already been assessed during your partners application',
      TEXT_ONE: 'Please connect to a different account or enter your income manually.',
      ACTION: 'Change income verification',
      TEXT_TWO: '',
    },
  },
  OPEN_BANKING_REVIEW_DESCRIPTION:
    'We’ve finishing verifying and estimating your monthly income. Please review and confirm the calculated amount.',
  CDR_REVIEW_EXPLANATION_TITLE: 'How we calculated your primary income after tax',
  CDR_REVIEW_EXPLANATION_DESCRIPTION:
    'To make things faster, we estimated your monthly take-home pay by identifying regular income deposits (like salary or wages) in your connected bank accounts. This figure represents the actual amount you receive after tax and deductions.',
  CDR_REVIEW_QUESTION_1: 'Why is my income lower than I expected?',
  CDR_REVIEW_QUESTION_1_ANSWER:
    'The total is based on your take-home pay. The money that actually hits your bank account/s after tax and deductions, not your gross salary. We also exclude one-off deposits like refunds or transfers to keep the estimate accurate.',
  CDR_REVIEW_QUESTION_2: 'What if I have other accounts with income?',
  CDR_REVIEW_QUESTION_2_ANSWER:
    'If your pay goes into an account you didn’t connect, our calculation will be incomplete. Select ‘No’ the estimate isn’t correct, and you can verify another bank account.',
  CDR_REVIEW_QUESTION_3: "I'm not paid monthly. Is this still right?",
  CDR_REVIEW_QUESTION_3_ANSWER:
    'Yes. To keep things simple, we automatically convert weekly or fortnightly payments into a single monthly amount so all your income is calculated the same way.',
};

export const GUARANTOR_DETAILS = {
  TITLE: 'Guarantor details',
  DESCRIPTION: 'Please select a guarantor from this list',
  SINGLE_DIRECTOR_MESSAGE: 'We have all the information we need. Please proceed to next page',
};

export enum EOpenBankingError {
  CONSENT_ENDUSER_DENIED = 'CONSENT_ENDUSER_DENIED',
  //Error while consent
  CONSENT_ENDUSER_INELIGIBLE = 'CONSENT_ENDUSER_INELIGIBLE',
  CONSENT_TIMEOUT = 'CONSENT_TIMEOUT',
  CONSUMERDATA_PROCESSING_ERROR = 'CONSUMERDATA_PROCESSING_ERROR',
  CONSENT_UPSTREAM_PROCESSING_ERROR = 'CONSENT_UPSTREAM_PROCESSING_ERROR',
  CONSENT_OTP_FAILURE = 'CONSENT_OTP_FAILURE',
  //Error after consent
  NO_INCOME = 'NO_INCOME',
  BUSINESS_ACCOUNT = 'BUSINESS_ACCOUNT',
  DUPLICATE_ACCOUNT = 'DUPLICATE_ACCOUNT',
}

export const openBankingErrorMapping: Record<EOpenBankingError, string> = {
  [EOpenBankingError.CONSENT_ENDUSER_DENIED]: 'We were unable to assess your income',
  //Error while consent
  [EOpenBankingError.CONSENT_ENDUSER_INELIGIBLE]: 'We were unable to assess your income',
  [EOpenBankingError.CONSENT_TIMEOUT]: 'Please try again—there was a problem connecting to your account.',
  [EOpenBankingError.CONSUMERDATA_PROCESSING_ERROR]: 'Please try again—there was a problem connecting to your account.',
  [EOpenBankingError.CONSENT_UPSTREAM_PROCESSING_ERROR]:
    'Please try again—there was a problem connecting to your bank.',
  [EOpenBankingError.CONSENT_OTP_FAILURE]:
    'Please check your login details and try again—we were not able to connect to your bank.',
  //Error after consent
  [EOpenBankingError.NO_INCOME]:
    'We couldn’t find any eligible accounts. Please connect the account where your income is paid.',
  [EOpenBankingError.BUSINESS_ACCOUNT]:
    'We couldn’t find any eligible accounts. Please connect the account where your income is paid.',
  [EOpenBankingError.DUPLICATE_ACCOUNT]:
    'We couldn’t find any eligible accounts. Please connect the account where your income is paid.',
};

export const HOUSEHOLD = {
  TITLE: 'Household details',
  DESCRIPTION: 'Tell us about your household and typical costs.',
  ADD_PARTNER: 'Would you like to add your partner as a joint applicant for this loan?',
  ADD_PARTNER_INFO:
    'Your application will be incomplete until the joint applicant completes the online application link sent to their email / sms.',
  PARTNER_DETAILS_HEADER: 'Enter your partner’s details to add them as a joint applicant.',
  PROPERTY_IN_AUSTRALIA: 'Do you own, or are in the process of purchasing, property in Australia?',
  HOUSING_COSTS_HEADER: 'Housing costs',
  DEBT_HEADER: 'Debt',
  CREDIT_HEADER: 'Credit',
  LIVING_EXPENSES_HEADER: 'Living expenses',
  MORTGAGE_LABEL: 'Do you pay a mortgage on your home?',
  MORTGAGE_SUBTITLE: 'Your primary residence - where you live.',
  MORTGAGE_REPAYMENT_HEADING: 'Home mortgage (repayments)',
  INVESTMENT_PROPERTIES_HEADER: 'Investment properties',
  INVESTMENT_PROPERTIES_LABEL: 'Do you have any investment properties?',
  INVESTMENT_PROPERTIES_SUBTITLE: 'NOT including your primary residence.',
  INVESTMENT_PROPERTIES_FREQUENCY_LABEL: 'Investment mortgages (repayments)',
  INVESTMENT_PROPERTIES_FREQUENCY_DESCRIPTION: 'Total repayments across all your investment properties.',
  MORTGAGE_REPAYMENTS_DESCRIPTION:
    'Total repayment amount for all properties *owned* by you and your partner. *Including investment properties.*',
  NO_MORTGAGE_REPAYMENTS: "We don't have any mortgage/s",
  RENT_LABEL: 'Do you pay for rent?',
  RENT_SUBTITLE: 'Total household rent',
  RENT_DESCRIPTION: 'How much are you and your partner’s rent?',
  RENT_MARRIED_DESCRIPTION: 'Total household rent.',
  RENT_SINGLE_DESCRIPTION: 'How much rent do you pay?',
  NO_RENT: "We don't have rent",
  OTHER_LOANS_LABEL: 'Do you have any other loans?',
  OTHER_LOANS_SUBTITLE: 'Car, personal, or student debt.',
  OTHER_LOANS_DESCRIPTION: 'Enter the combined repayment amount for your other loans.',
  NO_OTHER_LOANS: "We don't have other loans",
  CREDIT_CARDS_LABEL: 'Do you have any credit cards?',
  CREDIT_CARDS_AMOUNT_HEADER: 'Credit card spending limit',
  CREDIT_CARDS_DESCRIPTION: 'Total spending limit on all credit cards (not the balance owed).',
  NO_CREDIT_CARDS: "We don't have credit cards",
  NO_PARTNER_INCOME: 'Partner doesn’t have an income',
  HOUSE_HOLD_EXPENSES:
    'Combined monthly “day-to-day” costs (e.g., food, entertainment, utilities, transport, healthcare). *Do not include any rent, mortgage, or loan payments already mentioned.*',
  HOUSEHOLD_EXPENSES_LABEL: 'Household expenses',
  HOUSEHOLD_EXPENSES_SUBTITLE:
    "Include your regular living costs. Don't include rent, mortgage payments, or debt repayments.",
  MORTGAGE_REPAYMENTS_SOLO_DESCRIPTION:
    'How much is your mortgage repayments? Please include mortgages for any investment properties.',
  NO_MORTGAGE_SOLO_REPAYMENTS: "I don't have mortgage repayments",
  HOUSEHOLD_INCOME: 'Household income before tax',
  HOUSEHOLD_INCOME_DESCRIPTION:
    'Declare the total income earned by all income earners in your home before tax (i.e. gross total income of your home)',
  RENT_SOLO_DESCRIPTION: 'How much is your rent?',
  NO_RENT_SOLO: "I don't have rent",
  OTHER_LOANS_SOLO_DESCRIPTION: 'How much is your other loans?',
  NO_OTHER_LOANS_SOLO: "I don't have other loans",
  CREDIT_CARDS_SOLO_DESCRIPTION: 'What is the total of your credit card limits?',
  NO_CREDIT_CARDS_SOLO: "I don't have credit cards",
  HOUSE_HOLD_EXPENSES_SOLO:
    'Your expenses excluding, rent, mortgages and debt repayments. Please include all living expenses, e.g. food, entertainment, utilities, transport, healthcare, education, etc.',
  MARITAL_STATUS_OPTIONS: [
    { label: 'Married', value: 'married' },
    { label: 'De-facto', value: 'defacto' },
    { label: 'Single', value: 'single' },
  ],
  DEPENDANTS_OPTIONS: [
    { label: '0', value: '0' },
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3+', value: '3' },
  ],
  HOUSEHOLD_ASSESSMENT_INFORMATION_MESSAGE:
    'Our assessment assumes solar will reduce your household expenses (electricity costs).',
};

export const REPAYMENT_ACCOUNT = {
  TITLE: 'Repayment account',
  DESCRIPTION: (delayDays: string | number = '', financialProductId: string) => {
    const description = {
      [FINANCIAL_PRODUCT.FLEX]: `Please enter your bank account details for direct debit. If we accept your offer, these repayments will start ${delayDays} days after the installation of your purchase.`,
      [FINANCIAL_PRODUCT.BRIGHTE_PAY]: `You can choose to pay by bank account or debit/credit card. Your account will be debited in ${delayDays} days after installation.`,
      [FINANCIAL_PRODUCT.SME]: `You can choose to pay by bank account or debit/credit card. Your account will be debited in ${delayDays ?? 90} days after installation.`,
    };

    return (
      description[financialProductId] ??
      `Please enter your bank account details for direct debit. These repayments will start ${delayDays} days after the installation of your purchase.`
    );
  },
  BSB_INVALID: 'Please verify that the BSB number is correct',
  DIRECT_DEBIT_AGREEMENT:
    'I understand and agree to the terms and conditions governing the debit arrangements as set out in the',
  AUTHORISE_USER:
    'I authorise Brighte, through its financial institution, to debit the nominated account as per the terms agreed between the parties',
  SME_AUTHORISE_USER:
    'I authorise and request Brighte (User ID 502055/5022056) through its own financial institution, to debit your nominated account any amount Brighte has deemed payable by you.',
  TYPE: [
    { label: 'Direct debit', value: 'DEBIT' },
    { label: 'Credit card', value: 'CARD' },
  ],
  REPAYMENT_TYPE: 'Repayment type',
  DIRECT_DEBIT_HELPER_TEXT:
    'The account must be in the name of the applicant and cannot be a business account or belong to another person.',
  EXISTING_PAYMENT_METHOD_ERROR: 'Failed to create new payment method: token exists already',
  EXISTING_PAYMENT_METHOD_ERROR_DESCRIPTION:
    'We are having difficulties using this payment method at this time. Please try again with an alternate repayment method. ',
};

export const SUMMARY = {
  TITLE: 'Review and submit',
  DESCRIPTION: 'Your application is ready to submit. Review all your details above before submitting.',
  CONFIRMATION: (financeAmount: number) =>
    `I confirm I can afford to make the payment of ${formatDollars(financeAmount)} to the Brighte Payment Plan under my current and foreseeable financial circumstances.`,
  SOURCE_OF_FUNDS: 'Source of funds',
  SOURCE_DESCRIPTION: (financeAmount: number) =>
    `Please indicate the source of the ${formatDollars(financeAmount)} payment`,
  SOURCE_OF_FUNDS_OPTIONS: [
    { label: 'Current savings', value: PaymentFunds.CurrentSavings },
    { label: 'Mortgage redraw', value: PaymentFunds.MortgageRedraw },
    { label: 'Future earnings', value: PaymentFunds.FutureEarnings },
  ],
};

export const SUBMITTED = {
  TITLE: 'Application submitted',
  TITLE_JOINT: 'Primary application submitted',
};

export const APPROVED = {
  TITLE: 'Application approved',
  COMPLETED: 'Your application has been approved.',
};

export const REFERRED = {
  PAGE_TITLE: 'We’re almost there',
};

export const APPLICATION_UNDER_REVIEW = {
  TITLE: 'Application under review',
  CONTENT: 'Our team will review your application and they will be in contact if we need additional information.',
  ALERT_TITLE: 'Longer processing times due to high demand',
  ALERT_CONTENT: (
    <Text>
      We’re working hard to ensure that we can assess your application as quickly as possible. Due to the success of the{' '}
      <TextLink href="https://brighte.com.au/nsw" target="_blank" rel="noreferrer">
        NSW Home Energy Saver program
      </TextLink>
      , we’re experiencing longer processing times.
    </Text>
  ),
};

export const CUSTOMER_PORTAL = {
  HEADING: 'Track your application in the Brighte Portal',
  DESCRIPTION: 'You can track the status of your application at any time in the Brighte Portal.',
  GO_TO_CUSTOMER_PORTAL: 'Go to Brighte Portal',
};

export const OFFER_ACCEPTED = {
  TITLE: "Done! You're on your way to a brighter future",
  CONTENT: vendor =>
    `${vendor} will contact you soon to complete your project. Have a question? Call us on 1300-BRIGHTE or email us anytime.`,
  WHAT_HAPPENS_NEXT: 'What happens next?',
  BRIGHTE_SUPPORT_LINK: 'https://support.brighte.com.au/hc/en-gb/community/posts/900001010466-What-happens-next-',
};

export const ACCEPT_LOAN = {
  JOINT_DESCRIPTION:
    'If your loan is approved we will send you and your joint applicant a link to your loan contract. You will both need to accept this to finalise the loan.',
  DESCRIPTION:
    'If your loan is approved, we will send you a contract for signing. Please securely sign the contract to complete the loan process.',
  TITLE: 'Accept loan contract',
};

export const JOINT_APPLICATION = {
  description: (jointName: string | undefined = '') =>
    `We’ve sent a link to ${jointName} to complete their part of the application. Until then, we’re unable to process your application. Please make sure to follow up with them to complete the application in the next 24 hours.`,
  TITLE: 'Joint application pending',
  SUBMITTED_TITLE: 'Joint application submitted',
  JOINT_APPLICATION_PENDING:
    'We have sent your joint applicant a link to complete their part of the application. We will not be able to review your application until this happens.',
};

export const INSTALLATION_OF_PURCHASE = {
  DESCRIPTION:
    'Your vendor will be automatically notified when your application has been approved and will contact you to arrange installation.',
  APPROVED_DESCRIPTION:
    'Your vendor will be automatically notified and will proceed with installation of your purchase.',
  TITLE: 'Installation of purchase',
};

export const DECLINED_APPLICATION = {
  DECLINED_APPLICATION_REASON: [
    {
      title: 'Why an application can be unsuccessful?',
      message:
        'When we process an application, we check to ensure that repayments can be made. We also check an applicants credit history, which includes things such as number of previous application, defaults and repayment history.',
    },
    {
      title: 'How can you check your credit history?',
      message:
        'You can request a copy of your credit file for free by visiting the Equifax website. This will help you to understand why your application was unsuccessful.',
    },
  ],
  DECLINED_MESSAGE:
    "Your application was unsuccessful which means we're unable to offer you a :productName plan at this time. This could be for a number of reasons mentioned in the FAQs below.",
};

export const WITHDRAWN_APPLICATION = {
  TITLE: 'This application has been withdrawn.',
  MESSAGE:
    'This application has been withdrawn and can no longer be progressed. If you believe this is a mistake, please contact your vendor or Brighte.',
};

export const ALREADY_SUBMITTED_APPLICATION_STATUS = 'Quote has been converted.';
export const QUOTE_STATUSES = {
  COMPLETED: {
    icon: <Tick />,
    title: 'This application has been submitted already.',
    message: (
      <Text>
        You can download the{' '}
        <TextLink target="_blank" href="https://brighte.com.au/app">
          Brighte app
        </TextLink>{' '}
        to track the status or manage your application.
      </Text>
    ),
  },
  CANCELLED: {
    icon: <Exclamation />,
    title: "We're sorry, this quote has been cancelled.",
    message: <Text>Please contact your vendor to request a new quote.</Text>,
  },
  EXPIRED: {
    icon: <Exclamation />,
    title: "We're sorry, this quote has been expired.",
    message: <Text>Please contact your vendor to request a new quote.</Text>,
  },
};

export const START_REPAYMENTS = {
  description: (days: number | string, financialProductId: string) => {
    const description = {
      [FINANCIAL_PRODUCT.FLEX]: `If we accept your offer, Brighte will confirm your installation has occurred and ${days} days after that your automatic repayments will start. We will notify you when this will happen.`,
    };

    return (
      description[financialProductId] ??
      ` Brighte will confirm your installation has occurred and ${days} days after that your automatic repayments will start. We will notify you when this will happen.`
    );
  },
  TITLE: 'Repayment',
};

export const PAGE_FOOTER = {
  APPLY_BY_PHONE: 'Apply by phone',
  CALL_BRIGHTE: (
    <Text size="large" weight="semibold" tone="muted">
      Call Brighte on <TextLink href={`tel:${BRIGHTE_CONTACT_NUMBER}`}>{BRIGHTE_CONTACT_NUMBER}</TextLink>
    </Text>
  ),
  MENTION_CODE: 'Mention this code:',
  APPLICATION_CODE: 'BGLTEST',
  WHO_IS_BRIGHTE: 'Who is Brighte?',
  COMPANY_DESCRIPTION:
    'Brighte is Australia’s leading on the spot finance platform for solar, batteries and your next home improvements. Brighte enables you to enjoy your purchases now and pay it back over fortnightly instalments.',
  ACKNOWLEDGEMENT_OF_COUNTRY: 'Acknowledgement of Country',
  BRIGHTE_ACKNOWLEDGEMENT_OF_COUNTRY:
    'Brighte acknowledges Aboriginal and Torres Strait Islander peoples as the traditional custodians of our land – Australia. We pay our respect to them and their cultures and to the elders past, present and emerging. The Gadigal of the Eora Nation are the traditional custodians of the land on which Brighte’s office stands.',
  BRIGHTE_BUSINESS_DETAILS:
    ' Australian Business Number 74 609 165 906. Australian Credit License Number 508217. All applications are subject to Brighte’s credit approval criteria. Fees, Terms and Conditions apply.',
  BRIGHTE_CAPITAL: `© ${new Date().getFullYear()} Brighte Capital Pty Ltd`,
  TERMS_CONDITIONS: 'Terms and conditions',
  PRIVACY_POLICY: 'Privacy policy',
  TERMS: {
    [FINANCIAL_PRODUCT.FLEX]: 'https://brighte.com.au/support/brighte-flex-terms',
    [FINANCIAL_PRODUCT.ACT_SFA]: 'https://brighte.com.au/support/act-shs-sfa-terms',
    [FINANCIAL_PRODUCT.ELECTRIFY_2515]: 'https://brighte.com.au/electrify-2515-terms',
    [FINANCIAL_PRODUCT.BRIGHTE_PAY]: 'https://brighte.com.au/agreement/',
    [FINANCIAL_PRODUCT.BHESL]: 'https://brighte.com.au/support/home-energy-saver-loan-terms',
  },
  TERMS_LOANS: 'https://brighte.com.au/support/brighte-personal-loan-agreement',
};

export const CONTACT_DETAILS = {
  DESCRIPTION: '',
  TITLE: 'Contact details',
  CONFIRM_MOBILE: 'We’ll send a secure code to your mobile number to protect your account.',
  CONFIRM_EMAIL: 'We’ll send a secure code to your email address to protect your account.',
  TEMP_PASSWORD:
    'A temporary password to your Brighte account will be emailed to you shortly. You can track the status, manage your application',
  OTP_SENT_SMS: 'We sent you an sms with a secure code. Please enter it below.',
  OTP_SENT_EMAIL: 'We sent you an email with a secure code. Please enter it below.',
  OTP_INVALID: 'Invalid secure code',
  UPDATE_EMAIL:
    'The email address in your account is %USER_EMAIL%. If you change it, you must verify by entering a code sent to your new email.',
  UPDATE_MOBILE:
    'The mobile number in your account ends with %USER_MOBILE%. If you change it, you must verify by entering a code sent via SMS to your new number.',
};

export const KNOWN_ERROR_MESSAGES = {
  DEFAULT: 'We’re sorry, something went wrong. Please try again later.',
  INVALID_QUOTE: "The quote you're trying to access is invalid.",
  EXPIRED_QUOTE: "The quote you're trying to access has expired.",
  ALREADY_SUBMITTED_QUOTE: "The quote you're trying to access has already been submitted.",
  REPAYMENT_SUMMARY_NOT_FOUND: 'We are still loading your available finance terms. Please try again in a few minutes.',
  INTERNAL_SERVER_ERROR: 'Internal server error',
  USER_NOT_FOUND: 'Your user record could not be found.',
  USER_SUSPENDED: 'Your user record has been suspended.',
  FORBIDDEN: "We're sorry, you don't have permission to proceed.",
  VALIDATION_ERROR: 'Invalid user input. Please check the details and try again.',
  QUOTE_EXPIRED: 'This referral is no longer valid - please contact your vendor for a new referral.',
  UPDATE_APPLICATION_ERROR:
    "We've run into a problem and couldn’t save your progress. Please try again in a few minutes.",
  JOINT_APPLICANT_NOT_UNIQUE: APPLICATION_ERROR_MESSAGES.JOINT_APPLICANT_NOT_UNIQUE,
  IDENTITY_VERIFICATION_UNSUCCESSFUL: APPLICATION_ERROR_MESSAGES.IDENTITY_VERIFICATION_UNSUCCESSFUL,
};

// Backend error messages that are safe to surface to the user verbatim instead of the generic save error.
export const PASS_THROUGH_ERROR_MESSAGES = [
  KNOWN_ERROR_MESSAGES.JOINT_APPLICANT_NOT_UNIQUE,
  KNOWN_ERROR_MESSAGES.IDENTITY_VERIFICATION_UNSUCCESSFUL,
];

export const BUSINESS_DETAILS = {
  DESCRIPTION: 'Please enter some details about your business',
  TITLE: 'Business details',
  ACT_BUSINESS_STRUCTURE_OPTIONS: [
    { label: 'Company', value: EnterpriseStructure.Company },
    { label: 'Incorporated Association', value: EnterpriseStructure.IncorporatedAssociation },
    { label: 'Unincorporated Association', value: EnterpriseStructure.UnincorporatedAssociation },
  ],
  SME_STRUCTURE_OPTIONS: [
    { label: 'Partnership', value: EnterpriseStructure.Partnership },
    { label: 'Proprietary Limited Company', value: EnterpriseStructure.ProprietaryLimitedCompany },
    { label: 'Trust', value: EnterpriseStructure.Trust },
    { label: 'Sole Trader', value: EnterpriseStructure.SoleTrader },
  ],
  ACT_COMMUNITY_STRUCTURE_OPTIONS: [
    { label: 'Partnership', value: EnterpriseStructure.Partnership },
    { label: 'Proprietary Limited Company', value: EnterpriseStructure.ProprietaryLimitedCompany },
    { label: 'Trust', value: EnterpriseStructure.Trust },
    { label: 'Sole Trader', value: EnterpriseStructure.SoleTrader },
    { label: 'Other', value: EnterpriseStructure.Other },
  ],
  BUSINESS_DETAILS_FIELDS_DESCRIPTION: 'Tell us about your business',
  APPLICANT_DETAILS_FIELDS_DESCRIPTION: 'Tell us about yourself',
  EXTENDED_BUSINESS_DESCRIPTION: 'Tell us about your business',
  EXTENDED_PRIMARY_CONTACT_DESCRIPTION: 'Tell us about your primary contact',
  EXTENDED_BILLING_CONTACT_DESCRIPTION: 'Please indicate whether the billing contact is the same as the primary one',
  TRUST_DEED_TITLE: 'Trust deed',
  TRUST_DEED_DESCRIPTION: 'Please upload your trust deed here',
  DECLARATION_SOLE_TRADER:
    "I agree that I'm the owner of this company and authorised to complete this application for credit (if otherwise, you may not be able to complete this application)",
  DECLARATION_PARTNER:
    "I agree that I'm a partner of this company and authorised to complete this application for credit (if otherwise, you may not be able to complete this application)",
  DECLARATION_TRUSTEE:
    "I agree that I'm a trustee of this company and authorised to complete this application for credit (if otherwise, you may not be able to complete this application)",
  DECLARATION_DIRECTOR:
    "I agree that I'm the director of this company and will also be the guarantor of this loan (if otherwise, you may not be able to complete this application)",
  YEARS_IN_TRADING_OPTIONS: [
    { label: 'Less than 12 months', value: 'Less than 12 months' },
    { label: '1-2 years', value: '1-2 years' },
    { label: '2-3 years', value: '2-3 years' },
    { label: '3-4 years', value: '3-4 years' },
    { label: '4+ years', value: '4+ years' },
  ],
  NUMBER_OF_EMPLOYEES_OPTIONS: [
    { label: '<5', value: '<5' },
    { label: '5-20', value: '5-20' },
    { label: '20-50', value: '20-50' },
    { label: '50+', value: '50+' },
  ],
  INDUSTRY_CATEGORY_OPTIONS: [
    { label: 'Agriculture, Forestry and Fishing', value: 'Agriculture, Forestry and Fishing' },
    { label: 'Mining', value: 'Mining' },
    { label: 'Manufacturing', value: 'Manufacturing' },
    {
      label: 'Electricity, Gas, Water and Waste Services',
      value: 'Electricity, Gas, Water and Waste Services',
    },
    { label: 'Construction', value: 'Construction' },
    { label: 'Wholesale Trade', value: 'Wholesale Trade' },
    { label: 'Retail Trade', value: 'Retail Trade' },
    { label: 'Accommodation and Food Services', value: 'Accommodation and Food Services' },
    { label: 'Transport, Postal and Warehousing', value: 'Transport, Postal and Warehousing' },
    {
      label: 'Information Media and Telecommunications',
      value: 'Information Media and Telecommunications',
    },
    { label: 'Financial and Insurance Services', value: 'Financial and Insurance Services' },
    { label: 'Rental, Hiring and Real Estate Services', value: 'Rental, Hiring and Real Estate Services' },
    {
      label: 'Professional, Scientific and Technical Services',
      value: 'Professional, Scientific and Technical Services',
    },
    { label: 'Administrative and Support Services', value: 'Administrative and Support Services' },
    { label: 'Public Administration and Safety', value: 'Public Administration and Safety' },
    { label: 'Education and Training', value: 'Education and Training' },
    { label: 'Health Care and Social Assistance', value: 'Health Care and Social Assistance' },
    { label: 'Arts and Recreation Services', value: 'Arts and Recreation Services' },
    { label: 'Other Services', value: 'Other Services' },
  ],
  PROPERTY_STATUS_OPTIONS: [
    { label: 'Leased', value: 'Leased' },
    { label: 'Owned', value: 'Owned' },
    { label: 'Mortgage', value: 'Mortgage' },
  ],
};

export const productIds = {
  ACT_SHS: 'act-shs-loan',
  TAS_ESLS: 'tas-esls-loan',
  ACT_SLIP: 'act-slip-loan',
  BGL: 'brighte-green-loan',
  BPL: 'brighte-personal-loan',
  ACT_SFA: 'act-solar-for-apartments-loan',
  FLEX: 'brighte-flex',
  BNPL: '0-interest-payment-plan',
  ELECTRIFY_2515: 'electrify-2515',
  BRIGHTE_PAY: 'brighte-pay',
  HEUF_LOAN: 'brighte-heuf-green-loan',
  SME: 'brighte-business',
  ACT_COMMUNITY: 'act-community-loan',
  BHESL: 'brighte-home-energy-saver-loan',
};

export const PRODUCTS_WITH_PREFILLING_DISABLED = [productIds.ACT_SFA];

export const OWNERSHIP_DETAILS = {
  TITLE: 'Ownership details',
  DESCRIPTION_COMPANY: 'Tell us about the owners of the company',
  DESCRIPTION: 'Please provide the identification details of the Chairman, Secretary and Treasurer.',
  OWNERSHIP_TYPE_DESCRIPTION:
    'Please indicate whether company is registered by ASIC as a proprietary or public company',
  OWNERSHIP_TYPE_OPTIONS: [
    { label: 'Proprietary', value: OwnershipType.Proprietary },
    { label: 'Public', value: OwnershipType.Public },
  ],
  SME_OWNERSHIP_TYPE_DESCRIPTION: 'Please indicate the type of partners in business structure',
  SME_TRUST_OWNERSHIP_TYPE_DESCRIPTION: 'Please indicate the type of trustees in business structure',
  SME_OWNERSHIP_TYPE_OPTIONS: [
    { label: 'Individuals', value: OwnershipType.Individual },
    { label: 'Corporate entities', value: OwnershipType.Corporate },
  ],
  NUM_OF_DIRECTOR_DESCRIPTION: 'Please select how many directors are in the company and provide their names',
  SME_NUM_OF_DIRECTOR_DESCRIPTION: 'Please select how many directors are in the company and provide their names',
  TELL_ABOUT_BENEFICIAL: 'Tell us about beneficial owners',
  BENEFICIAL_DESCRIPTION:
    'Anyone who owns 25% or more of the company is a beneficial owner. How many beneficial owners does your company have?',
  BENEFICIAL_OWNER: 'Beneficial Owner',
  BENEFICIAL_IDENTIFICATION_DESCRIPTION: 'Please provide the identification details for each of the beneficial owners.',
  NUMBER_OF_BENEFICIAL_OWNER_OPTIONS: 'Select number of beneficial owners',
  PRIVACY_AND_CREDIT_TEXT:
    "I acknowledge that I have read Brighte's Privacy Policy and Credit Reporting Privacy Policy and I consent to Brighte obtaining and using credit information in a credit report about me when assessing the application for commercial credit made in this application.",
  PRIVACY_AND_CREDIT_STATEMENT: (
    <Text>
      I acknowledge that I have read Brighte&apos;s{' '}
      <TextLink href="https://brighte.com.au/privacy/" target="_blank" rel="noreferrer">
        Privacy Policy and Credit Reporting Privacy Policy
      </TextLink>{' '}
      and I consent to Brighte obtaining and using credit information in a credit report about me when assessing the
      application for commercial credit made in this application.
    </Text>
  ),
};

export const BENEFICIAL_OWNER_DETAILS = {
  TITLE: 'Beneficial owners',
  BENEFICIAL_DESCRIPTION:
    'Anyone who owns 10% or more of the company is a beneficial owner. How many beneficial owners does your company have?',
  CORPORATE_ENTITIES_LABEL: 'Corporate entities associated with the business',
  CORPORATE_ENTITIES_DESCRIPTION:
    'Please list the full details of all corporate entities that are partners associated with the business, including: legal entity name, trading name, ABN, ACN, email/phone, address. Submit each entity on a separate line.',
};

export const ENV_VARIABLES = {
  DATADOG_RUM_APP_ID: import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID,
  DATADOG_RUM_CLIENT_TOKEN: import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN,
  IS_DEV: import.meta.env.DEV,
  ENV: import.meta.env.VITE_ENVIRONMENT,
  GIT_COMMIT: import.meta.env.VITE_GIT_COMMIT,
  LAUNCH_DARKLY_CLIENT_ID: import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
  AUTH_CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID ?? 'KlkJpkacqZhYgRRDIDpB1oLk67AoLY71',
  AUTH_DOMAIN: import.meta.env.VITE_AUTH_DOMAIN ?? 'auth.staging.cloud.brighte.com.au',
};

export const CUSTOMER_PORTAL_URL: Record<string, string> = {
  staging: 'https://my.staging.cloud.brighte.com.au',
  uat: 'https://my.uat.cloud.brighte.com.au',
  prod: 'https://my.brighte.com.au',
};

export const getCustomerPortalUrl = (): string => CUSTOMER_PORTAL_URL[ENV_VARIABLES.ENV] ?? CUSTOMER_PORTAL_URL.staging;

export const BIO_CHECK_MESSAGE =
  'In our commitment to protect you from identity theft, we may request you to complete a biometric identity check. This process involves a video comparison with your photo ID. Should this step be necessary, we will send you an SMS with a link to complete the verification.';

export const PAYMENT_DELAY_DAYS = (quote?: ModifiedApplicationQuote | null): number =>
  quote?.promotion?.productConfiguration?.firstPaymentDelayDays ??
  (quote?.productConfiguration?.firstPaymentDelayDays as number);

export const instantApprovalProducts = [productIds.FLEX, productIds.BNPL, productIds.BRIGHTE_PAY];
export const MAX_SYNC_STATUS_WAIT = 30;
// CT-3287: last-resort deadline (seconds) for tearing down the session on the status page. The
// teardown normally waits for the status query to settle; if that query hangs or sits in retry
// backoff forever the credentials would otherwise stay in local storage. Set well beyond the
// polling window above so it only ever fires for a genuinely stuck request.
export const SESSION_TEARDOWN_DEADLINE = MAX_SYNC_STATUS_WAIT * 2;
export const BGL_MAX_INTEREST_RATE = 4.5;

export const OFFER_ACCEPTANCE = {
  TITLE: 'your offer is ready to accept',
  DESCRIPTION: "Read the full loan contract, then confirm you've read and understood it to accept our offer.",
  LOAN_COPY_NOTE: ' A copy of this loan contract will be emailed to you upon acceptance.',
  LOAN_CONFIRMATION:
    'I confirm that I have read and understood the loan contract and agree to enter into a contract for finance with Brighte on these terms.',
  ACCEPT: 'I accept',
  CONTRACT_COPY: 'A copy of this loan contract will be emailed to you upon acceptance.',
  CONTRACT_REVIEW_ERROR: 'You must read to the end of your loan contract before you continue.',
};

export const LOAN_CONTRACT = {
  KEEP_READING: 'Keep reading',
  PROGRESS: (percent: number) => `${percent}% read`,
  MESSAGE: (vendorName: string) => (
    <>
      <Text>
        Thank you for applying for a Loan to fund your recent purchase with your vendor {vendorName}. Your application
        has been successful.
      </Text>
      <Text>
        Brighte Capital Pty Limited (Australian Credit Licence 508217) offers to enter into a loan contract with you on
        the terms of the Loan Schedule (“Schedule”) and terms and conditions set out below.
      </Text>
    </>
  ),
  LOAN_SCHEDULE: 'Loan Schedule',
  FINANCIAL_TABLE: 'Financial Table',
  FEES: 'Fees',
  FIRST_PAYMENT_DELAYS: days => `${days} days after installation`,
  ESTABLISHMENT_FEE_SUB: '(Payable on the day we advance the loan from the amount of the advance)',
  WEEKLY_ACCOUNT_KEEPING_FEE_SUB: amount =>
    `Commencing 2 weeks before the first repayment date and included as ${amount} in your fortnightly repayment`,
  LATE_PAYMENT_FEE_SUB: 'If you miss a repayment due date',
  INTRODUCER_FEE_SUB: '(Payable on the day we advance the loan from the amount of the advance)',
  LOAN_CONTRACT_TABLE_FIELDS: {
    dated: 'Dated',
    loan: 'Loan',
    annualPercentageRate: 'Annual Percentage Rate',
    totalInterestPayable: 'Total Interest Payable',
    totalMinimumAmountPayable: 'Total minimum amount payable',
    noOfFortnights: 'Number of fortnight repayments',
    firstRepaymentDate: 'First repayment date',
    establishmentFee: 'Establishment Fee',
    establishmentFeeSub: '(Payable on the day we advance the loan from the amount of the advance)',
    weeklyAccountKeepingFee: 'Weekly Account Keeping Fee',
    latePaymentFee: 'Late Payment Fee',
    introducerFee: 'Introducer Fee',
    totalFees: 'Total Fees',
    amountToPay: ({ term, paymentCycle }) =>
      `How much you'll pay each ${paymentCycle.toLowerCase()} for the next ${term} ${term <= 1 ? 'year' : 'years'}`,
    repaymentChanges:
      'We might change your repayments to ensure that you can pay your loan within the loan term. Brighte will contact you if we believe this is necessary. We can also change the fee amount or waive any fee as long as you are no worse off by us doing so.',
    commissionAmount:
      'A commission in the amount of the Introducer Fee plus GST is payable to your vendor by us for the introduction of your Loan.',
  },
};

export const ELECTRIFY_CASH_REPAYMENT_TERMS = {
  term: 1,
  amount: 0,
  dateMaturity: '',
  interestRate: 0,
  paymentCycle: 'f',
  termInMonths: 1,
  interestCycle: 'f',
  totalInterest: 0,
  totalPayments: 0,
  paymentCountRegular: 0,
  termNumberOfPayments: 1,
  paymentRegularFirstDate: '',
};

export const MONTHS_IN_A_YEAR = 12;

export const FORTNIGHTS_IN_A_YEAR = 26;
export enum KnownErrors {
  NotFound = 'No Quote found',
  ValidationError = 'Validation Error',
  QuoteExpired = 'Quote belongs to a product configuration that has expired.',
}

export enum FinancialFieldTypes {
  PRIMARY = 'PRIMARY',
  JOINT = 'JOINT',
  MORTGAGE = 'MORTGAGE',
  RENT = 'RENT',
  INVESTMENT_PROPERTIES = 'INVESTMENT_PROPERTIES',
}

export const FINANCIAL_FIELD_TYPE_TEXT = {
  [FinancialFieldTypes.PRIMARY]: 'income after tax',
  [FinancialFieldTypes.JOINT]: "partner's income",
  [FinancialFieldTypes.MORTGAGE]: 'mortgage repayment',
  [FinancialFieldTypes.INVESTMENT_PROPERTIES]: 'investment properties repayment',
  [FinancialFieldTypes.RENT]: 'rent',
};

export const FINANCIAL_MONTHLY_THRESHOLD_CONFIGURATION = {
  income: {
    maximum: 15000,
    minimum: 2000,
  },
  mortgageRepayment: {
    maximum: 10000,
    minimum: 500,
  },
  rent: {
    maximum: 5000,
    minimum: 5,
  },
  investmentProperties: {
    maximum: 10000,
    minimum: 500,
  },
};

export const OPEN_BANKING_POLLING_INTERVAL = 10 * 1000;

export const OPEN_BANKING_TIMEOUT_DURATION = 10 * 60 * 1000; // 10 minutes for production

export const ONE_MINUTE_MS = 60 * 1000; // 1 minute in milliseconds
export const TWO_MINUTES_MS = 2 * 60 * 1000; // 2 minutes in milliseconds
export const THREE_MINUTES_MS = 3 * 60 * 1000; // 3 minutes in milliseconds

// --- Land title search (CT-3221) -------------------------------------------
// Equifax resolves most NSW titles in under two minutes, but the tail is long:
// in the 29 Jun - 28 Jul sample, half of all successful searches landed after
// the previous 3-minute cap had already given up. 5 minutes recovers the near
// misses without leaving the customer staring at a spinner indefinitely.
export const LAND_TITLE_POLL_INTERVAL_MS = 3 * 1000;
export const LAND_TITLE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Progressive wait copy, one entry per elapsed minute (index = whole minutes
 * waited, clamped to the last entry). Mirrors the CDR pattern in
 * OpenBankingIncomeLoadingDetails: the customer needs to know work is still
 * happening and that leaving the page is what breaks it.
 *
 * Deliberately no false progress ("almost done", "90% complete") — we have no
 * signal from Equifax to justify it, and a promise that doesn't land is worse
 * than a long wait.
 */
export const LAND_TITLE_SEARCH_WAIT_MESSAGES = [
  {
    title: 'Looking up your property title',
    subtitle: 'We’re checking the certificate of title with NSW Land Registry Services',
  },
  {
    title: 'Confirming who owns the property',
    subtitle: 'This tells us who needs to provide income details, so we only ask the right people',
  },
  {
    title: 'The registry is taking a little longer',
    subtitle: 'Most searches come back within a couple of minutes. Busy periods can stretch that out.',
  },
  {
    title: 'We’re still on it',
    subtitle: 'There’s nothing for you to do — just keep this page open and your results will appear here',
  },
  {
    title: 'Thanks for your patience',
    subtitle: 'If the registry doesn’t respond shortly, our team will pick this up and be in touch',
  },
] as const;

export const OPEN_BANKING_CONSENTS_TIMEOUT_DURATION = 15 * 1000;

export const OPEN_BANKING_CONSENTS_POLLING_INTERVAL = 5 * 1000;

export const APPLICATION_FORM = (name?: string) => ({
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: `${name} Subsidy Application`,
});

export const LAND_TITLE_ELIGIBILITY_CRITERIA = [
  {
    icon: <CurrencyDollarIcon size="small" />,
    text: (
      <Text size="standard">
        Your household income needs to be{' '}
        <Text as="span" weight="semibold" inline>
          less than $210,000 per year in taxable income
        </Text>
      </Text>
    ),
  },
  {
    icon: <HomeIcon size="small" />,
    text: (
      <Text size="standard">
        You need to be the{' '}
        <Text as="span" weight="semibold" inline>
          homeowner
        </Text>
      </Text>
    ),
  },
  {
    icon: <DocumentTextIcon size="small" />,
    text: (
      <Text size="standard">
        You will need to upload the{' '}
        <Text as="span" weight="semibold" inline>
          Notice of Assessment for each person
        </Text>{' '}
        on the property title.
      </Text>
    ),
  },
];

// NSW Government caps combined household taxable income for this program.
export const HOUSEHOLD_TAXABLE_INCOME_CAP = 210000;
export const INCOME_OVER_CAP_ERROR =
  "Unfortunately you're not eligible. This program is only available to households with a taxable income of $210,000 or less.";
